import React, { useEffect, useRef, useState } from "react"
import DataTable from "react-data-table-component"
import { ChevronDown, ChevronLeft, ChevronRight, Edit, Trash } from "react-feather"
import ReactPaginate from "react-paginate"
import classNames from "classnames"

import "../../assets/scss/plugins/extensions/react-paginate.scss"
import "../../assets/scss/pages/data-list.scss"
import fetchApi from "../../utility/context/fetchApi"
import { connect } from "react-redux"
import { promosSelector, tokenSelector } from "../../redux/selectors"
import DataListSidebar from "./PromoDataListSidebar"
import { comparators, CustomHeader, getSliceData, getSortIndex, getTotalPages, typeDate, typeString } from "./utils"
import { setPromos } from "../../redux/actions"
import BasicSweetCallback from "../../extensions/sweet-alert/SweetAlertCallback"
import {AllHtmlEntities} from 'html-entities'

const entities = new AllHtmlEntities();

const ActionsComponent = ({ row, editRow, deleteRow }) => {
    return (
        <div className="data-list-action minus-6">
            <Edit
                className="cursor-pointer control-icon"
                size={20}
                onClick={() => {
                    return editRow(row)
                }}
            />
            <Trash
                className="cursor-pointer control-icon"
                size={20}
                onClick={() => {
                    deleteRow(row)
                }}
            />
        </div>
    )
}

function Promos(props) {
    const [data, setData] = useState([])
    const [total, setTotal] = useState(1)
    const [page, setPage] = useState(0)
    const [limit, setLimit] = useState(4)
    const [totalPages, setTotalPages] = useState(1)
    const [sortIndex, setSortIndex] = useState([1, 2])
    const [sidebar, setSidebar] = useState(false)
    const [currentData, setCurrentData] = useState(null)
    const [filter, setFilter] = useState('')
    const [sort, setSort] = useState({ selector: 'createdAt', type: 'asc', typeData: typeDate })
    const refAlert = useRef()

    useEffect(() => {
        const allDataSorted = props.totalData.slice().sort(comparators[sort.typeData](sort.selector))
        if (sort.type === 'desc') allDataSorted.reverse()
        if (filter) {
            const filteredData = allDataSorted.filter(({ text }) => text.toLowerCase().includes(filter.toLocaleLowerCase()))
            setData(filteredData)
            setTotal(filteredData.length)
            setTotalPages(1)
            setPage(0)
            setSortIndex([1, filteredData.length])
        } else {
            setData(getSliceData(allDataSorted, page, limit))
            setTotal(allDataSorted.length)
            setTotalPages(getTotalPages(allDataSorted, limit))
            setSortIndex(getSortIndex(page, limit, allDataSorted.length))
        }
    }, [page, props.totalData, limit, filter, sort, props.token])

    const handleLimit = (limit) => {
        setPage(0)
        setLimit(limit)
    }

    const handleSort = ({ selector, type: typeData }, type) => {
        setSort({ selector, type, typeData })
    }

    const cancelSidebar = () => {
        setSidebar(false)
        setCurrentData(null)
    }

    const addData = (data) => {
        fetchApi({
            url: '/promo',
            method: 'POST',
            token: props.token,
            body: data
        }).then((doc) => {
            props.setPromos([doc, ...props.totalData])
            setPage(0)
            setCurrentData(null)
        })
    }

    const updateData = ({ _id, ...data }) => {
        fetchApi({
            url: `/promo/${_id}`,
            method: 'PATCH',
            token: props.token,
            body: data
        }).then((res) => {
            props.setPromos(props.totalData.map((node) => {
                if (node._id === res._id) return res
                return node
            }))
            setCurrentData(null)
        })
    }

    const removeData = ({ _id }) => {
        fetchApi({
            url: `/promo/${_id}`,
            method: 'DELETE',
            token: props.token,
        }).then(() => {
            props.setPromos(props.totalData.filter((node) =>
                node._id !== _id
            ))
        })
    }

    const columns = [
        {
            name: "Text RU",
            type: typeString,
            selector: "text",
            sortable: true,
            cell: ({ text }) => {
                return <span dangerouslySetInnerHTML={{__html: entities.decode(text)}}/>
            },
            grow: 1
        },
        {
            name: "Text EN",
            type: typeString,
            selector: "textEn",
            sortable: true,
            cell: ({ textEn }) => {
                return <span dangerouslySetInnerHTML={{__html: entities.decode(textEn)}}/>
            },
            grow: 1
        },
        {
            name: "Active",
            selector: "active",
            width: '150px',
            cell: ({ active }) => {
                return String(active)
            },
            grow: 1
        },
        {
            name: "Target",
            selector: "target",
            width: '150px',
            grow: 1
        },
        {
            name: "Actions",
            maxWidth: '100px',
            width: '100px',
            cell: row => (
                <ActionsComponent
                    row={row}
                    editRow={(row) => {
                        setCurrentData(row)
                        setSidebar(true)
                    }}
                    deleteRow={(row) => refAlert.current.show(row)}
                />
            )
        }
    ]

    return <div className={`data-list list-view`}>
        <BasicSweetCallback refAlert={refAlert}  onConfirm={(row) => {
                    removeData(row)
                }}/>
        <DataTable
            columns={columns}
            data={data}
            pagination
            paginationServer
            paginationComponent={() => (
                <ReactPaginate
                    previousLabel={<ChevronLeft size={15} />}
                    nextLabel={<ChevronRight size={15} />}
                    breakLabel="..."
                    breakClassName="break-me"
                    pageCount={totalPages}
                    containerClassName="vx-pagination separated-pagination pagination-end pagination-sm mb-0 mt-2"
                    activeClassName="active"
                    initialPage={page}
                    onPageChange={page => setPage(page.selected)}
                />
            )}
            noHeader
            subHeader
            responsive
            pointerOnHover
            sortIcon={<ChevronDown />}
            sortServer
            onSort={handleSort}
            subHeaderComponent={
                <CustomHeader
                    handleSidebar={setSidebar}
                    handleFilter={(value) => setFilter(value)}
                    handleRowsPerPage={handleLimit}
                    rowsPerPage={limit}
                    total={total}
                    index={sortIndex}
                />
            }
        />
        <DataListSidebar
            show={sidebar}
            data={currentData}
            updateData={updateData}
            addData={addData}
            cancelSidebar={cancelSidebar}
        />
        <div
            className={classNames("data-list-overlay", {
                show: sidebar
            })}
            onClick={cancelSidebar}
        />
    </div>
}

const mapStateToProps = state => {
    return {
        totalData: promosSelector(state),
        token: tokenSelector(state)
    }
}

export default connect(mapStateToProps, { setPromos })(Promos)
